import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NgbModal, NgbNav } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { concatMap, take } from 'rxjs/operators';
import { FiltersEmitModel, DID, VerificationData, DID_TYPE, DID_STATUS } from 'voxloud-types/dist/models';

import { Company } from 'src/app/domains/companies/models';
import { DidsService, VerificationDataService } from 'src/app/domains/dids/services';
import { Subscription, SUBSCRIPTION_STATUS } from 'src/app/domains/subscription/models';
import { SubscriptionService } from 'src/app/domains/subscription/services';
import { Paginated } from 'src/app/shared/paginated.model';
import { NotificationService } from 'src/app/shared/services/notification/notification.service';

import { AddPortDidPopupComponent } from '../add-port-did-popup/add-port-did-popup.component';
import { AddTestingDidPopupComponent } from '../add-testing-did-popup/add-testing-did-popup.component';
import { CreateDidOrderPopupComponent } from '../create-did-order-popup/create-did-order-popup.component';
import { CreateVerificationDataPopupComponent } from '../create-verification-data-popup/create-verification-data-popup.component';
import { ReplaceWithExistingDidPopupComponent } from '../replace-with-existing-did-popup/replace-with-existing-did-popup.component';
import { ReplaceWithNewDidPopupComponent } from '../replace-with-new-did-popup/replace-with-new-did-popup.component';
import { UpdateProviderPopupComponent } from '../update-provider-popup/update-provider-popup.component';
import { ActivationLinkService } from 'src/app/domains/sales/activation-link/services';
import { Plan } from 'src/app/domains/sales/activation-link/models/activation-link.model';

const PAGE_SIZE = 10;
const ALL_DIDS_LIMIT = 10000;

@Component({
  selector: 'gdp-company-dids',
  templateUrl: './company-dids.component.html',
  styleUrls: ['./company-dids.component.scss']
})
export class CompanyDidsComponent implements OnInit {
  @Input() company: Company;
  active: string;
  currentTab = 'dids-list';
  @ViewChild('nav', { static: false }) nav: NgbNav;
  public TAB_ICONS = {
    'dids-list': 'icon-small-telephone',
    'verification-data': 'icon-small-template'
  }
  public allDids: DID[] = [];
  public dids: DID[] = [];
  public verificationDataList: VerificationData[];
  public loading = {
    subscription: false,
    dids: false,
    verificationData: false,
    info: false
  };
  public pagination = {
    page: 0,
    empty: true,
    totalPages: 0
  };
  public pageSize: number = PAGE_SIZE;
  public subscription: Subscription;
  public SUBSCRIPTION_STATUS = SUBSCRIPTION_STATUS;
  private filters: FiltersEmitModel;

  public isAddingTemporaryDid: boolean = false;
  public currentPlan: Plan;

  constructor(
    private didsService: DidsService,
    private verificationDataService: VerificationDataService,
    private notification: NotificationService,
    private modalService: NgbModal,
    private translate: TranslateService,
    private subscriptionService: SubscriptionService,
    private activationLinkService: ActivationLinkService
  ) { }

  ngOnInit() {
    this.filters = {
      company_id: this.company.companyId
    };
    this.getSubscription();
    this.loadAllDids();
    this.getDids(this.filters);
    this.getVerificationData();
  }

  public switchTab(tabId: string): void {
    this.nav.select(tabId);
    this.currentTab = tabId;
  }

  getSubscription() {
    this.loading.subscription = true;
    this.subscriptionService.getSubscription(this.company.companyId)
    .pipe(
      concatMap((res: Subscription) => {
        this.subscription = res;
        return this.activationLinkService.getPlanById(res.plan.id)
      })
    ).subscribe((res: Plan) => {
      this.currentPlan = res;
      this.loading.subscription = false;
    }, () => {
      this.loading.subscription = false;
    });
  }

  private loadAllDids(): void {
    this.didsService.getDids(0, ALL_DIDS_LIMIT, { company_id: this.company.companyId })
      .subscribe(
        (res: Paginated<DID>) => { this.allDids = res.content; },
        () => { this.allDids = []; }
      );
  }

  getDids(filters: FiltersEmitModel) {
    this.loading.dids = true;
    this.didsService.getDids(this.pagination.page, this.pageSize, filters)
      .subscribe(
        (res: Paginated<DID>) => {
          this.dids = res.content;
          this.pagination.totalPages = this.dids.length !== 0 ? res.totalPages : 1;
          this.loading.dids = false;
        },
        () => {
          this.dids = [];
          this.pagination.totalPages = 1;
          this.pagination.page = 0;
          this.loading.dids = false;
        }
      );
  }

  onFilter(filters: FiltersEmitModel) {
    this.filters = filters;
    this.pagination.page = 0;
    this.getDids(filters);
  }

  getVerificationData() {
    this.loading.verificationData = true;
    this.verificationDataService.getVerificationDataByCompanyId(this.company.companyId)
      .subscribe(
        (res: VerificationData[]) => {
          this.verificationDataList = res;
          this.loading.verificationData = false;
        },
        () => {
          this.verificationDataList = [];
          this.loading.verificationData = false;
        }
      );
  }

  selectPageSize(event: number) {
    this.pageSize = event;
    this.pagination.page = 0;
    this.getDids(this.filters);
  }

  changeCurrentPage(event: number) {
    this.pagination = {
      page: event,
      empty: this.dids.length === 0,
      totalPages: this.pagination.totalPages
    };
    this.getDids(this.filters);
  }

  onReplaceWithExistingDid(did: DID) {
    const modal = this.modalService.open(ReplaceWithExistingDidPopupComponent, {
      size: 'lg'
    });
    modal.componentInstance.did = did;
    modal.componentInstance.companyId = this.company.companyId;
    modal.componentInstance.onReplaceDid.pipe(take(1))
      .subscribe((res: string) => {
        const message = this.translate.instant(
          'company.dids.replace-with-existing-did.popup.success',
          { old: did.number, new: res }
        );
        this.notification.success(message);
        this.loadAllDids();
        this.getDids(this.filters);
      });
  }

  onUpdateDidProvider(did: DID) {
    const modal = this.modalService.open(UpdateProviderPopupComponent, {
      size: 'lg'
    });
    modal.componentInstance.did = did;
    modal.componentInstance.companyId = this.company.companyId;
    modal.componentInstance.onReplaceDid.pipe(take(1))
      .subscribe((res: string) => {
        const message = this.translate.instant(
          'company.dids.update-provider.popup.success',
        );
        this.notification.success(message);
        this.getDids(this.filters);
      });
  }

  onReplaceWithNewDid(did: DID) {
    const modal = this.modalService.open(ReplaceWithNewDidPopupComponent, {
      size: 'lg'
    });
    modal.componentInstance.did = did;
    modal.componentInstance.companyId = this.company.companyId;
    modal.componentInstance.onReplaceDid.pipe(take(1))
      .subscribe((res: string) => {
        const message = this.translate.instant(
          'company.dids.replace-with-did.popup.success',
          { old: did.number, new: res }
        );
        this.notification.success(message);
        this.loadAllDids();
        this.getDids(this.filters);
      });
  }

  openCreateVerificationDataPopup() {
    const modal = this.modalService.open(CreateVerificationDataPopupComponent, {
      size: 'lg'
    });
    modal.componentInstance.companyId = this.company.companyId;
    modal.componentInstance.onCreateVerificationData.pipe(take(1)).subscribe(() => {
      this.getVerificationData();
      this.notification.success('company.dids.verification-data.create-success');
    });
  }

  openCreateDidOrderPopup() {
    const modal = this.modalService.open(CreateDidOrderPopupComponent, {
      size: 'lg'
    });
    modal.componentInstance.companyId = this.company.companyId;
    modal.componentInstance.subscription = this.subscription;
    modal.componentInstance.planAddonHandling = this.currentPlan.meta_data.shop.phone_number.addon_management;
    modal.componentInstance.onCreateDid.pipe(take(1))
      .subscribe((res: string) => {
        const message = this.translate.instant(
          'company.dids.dids-list.popup.create.success',
          { new: res }
        );
        this.notification.success(message);
        this.loadAllDids();
        this.getDids(this.filters);
      });
  }

  openAddPortDidPopup() {
    const modal = this.modalService.open(AddPortDidPopupComponent, {
      size: 'lg'
    });
    modal.componentInstance.companyId = this.company.companyId;
    modal.componentInstance.subscription = this.subscription;
    modal.componentInstance.planAddonHandling = this.currentPlan.meta_data.shop.phone_number.addon_management;
    modal.componentInstance.onAddExistingOrPortedDid.pipe(take(1)).subscribe((message: string) => {
      this.loadAllDids();
      this.getDids(this.filters);
      this.notification.success(message);
    });
    modal.dismissed.subscribe(() => {
      this.didsService.setAddPortDidFormValue(null);
    });
    modal.closed.subscribe(() => {
      this.didsService.setAddPortDidFormValue(null);
    });
  }

  openAddTestingDidPopup(): void {
    const modal = this.modalService.open(AddTestingDidPopupComponent, {
      size: 'lg'
    });
    modal.componentInstance.companyId = this.company.companyId;
    modal.componentInstance.planAddonHandling = this.currentPlan.meta_data.shop.phone_number.addon_management;
    modal.componentInstance.onAddTestingDid.pipe(take(1)).subscribe((numb: string) => {
      this.loadAllDids();
      this.getDids(this.filters);
      const message = this.translate.instant('company.dids.add-testing-did.success', { value: numb });
      this.notification.success(message);
    });
  }

  onAddTemporaryDid(): void {
    this.isAddingTemporaryDid = true;
    this.notification.showConfirmation('company.dids.add-temporary-did.confirmation').then((result: string) => {
      if(result === 'ok') {
        this.didsService.addTemporaryDid(this.company.companyId).subscribe((response: DID) => {
          const message = this.translate.instant('company.dids.add-temporary-did.success', {value: response.number});
          this.notification.success(message);
          this.loadAllDids();
          this.getDids(this.filters);
          this.isAddingTemporaryDid = false;
        }, () => {
          this.notification.error('company.dids.add-temporary-did.failure');
          this.isAddingTemporaryDid = false;
        });
      }
      else {
        this.isAddingTemporaryDid = false;
      }
    })
  }

  onFetchDidsChanges(): void {
    this.loadAllDids();
    this.getDids(this.filters);
  }

  private get activeDids(): DID[] {
    return this.allDids.filter(d => d.status !== DID_STATUS.UNDER_TESTING);
  }

  private get activeDidsCount(): number {
    return this.activeDids.length;
  }

  get availableSlotsString(): string {
    const phoneNumberCount = this.subscription?.phone_number_count || 0;
    return `${Math.max(this.activeDidsCount, 0)}/${phoneNumberCount}`;
  }

  get availableSlotsByPercentage(): number {
    const phoneNumberCount = this.subscription?.phone_number_count || 0;
    if (!phoneNumberCount) return 0;
    const percentage = (this.activeDidsCount / phoneNumberCount) * 100;
    return Math.max(0, Math.min(100, percentage));
  }

  get hasPhoneNumbersBundles(): boolean | undefined {
    const phoneCount = Number(this.subscription?.phone_number_count ?? 0);

    if (phoneCount <= 0) {
      return false;
    }

    if (!this.subscription?.meta_data?.shop) {
      return false;
    }

    const shopData = this.subscription.meta_data.shop;
    const bundle = shopData?.phone_number_bundle;

    return !!bundle && (
      (bundle.monthly?.length ?? 0) > 0 ||
      (bundle.one_time?.length ?? 0) > 0
    );
  }

  get internationalDidsCount(): number {
    return this.activeDids.filter(d => d.did_type === DID_TYPE.TOLL_FREE).length;
  }

  get nationalDidsCount(): number {
    return this.activeDids.filter(d => d.did_type === DID_TYPE.GEOGRAPHIC).length;
  }
}